import { cn } from '@/utils';
import { Tooltip, Typography } from '@analytical-alley/ui';
import { ReactNode } from 'react';

/**
 * The tooltip is cursor-anchored and closes without a safe polygon, so it can
 * never be hovered into and its content can never be scrolled. Everything below
 * exists to keep a full variable list inside one viewport-height box:
 *
 * - past `WIDE_LIST_THRESHOLD` names the list goes to three columns, which is
 *   shorter than two and needs a wider box to hold the longest names;
 * - `MAX_LISTED` is the last resort. A real dataset here has 27 media variables,
 *   so the cap must sit well above that or the list stops being an answer.
 */
const MAX_LISTED = 36;
const WIDE_LIST_THRESHOLD = 14;

const isWide = (variables: string[]) =>
    variables.length > WIDE_LIST_THRESHOLD;

const VariableList = ({ variables }: { variables: string[] }) => {
    const listed = variables.slice(0, MAX_LISTED);
    const hidden = variables.length - listed.length;

    return (
        <>
            <ul
                // `list-outside` + padding so a name too long for its column
                // hangs under itself instead of running back under the marker,
                // where it would read as a separate variable.
                className={cn(
                    'gap-6 my-1 pl-5 list-disc list-outside',
                    isWide(listed) ? 'columns-3' : 'columns-2',
                )}
            >
                {listed.map((variable) => (
                    <li key={variable} className="break-inside-avoid">
                        {variable}
                    </li>
                ))}
            </ul>
            {hidden > 0 && <p>…and {hidden} more.</p>}
        </>
    );
};

/**
 * Explains which variables the Total Media ROI value was calculated across.
 *
 * Rendered only when the dashboard's `modeledVariablesOnlyInTotalMediaRoi`
 * setting is on, where the KPI deliberately covers fewer variables than the table
 * below it. Naming both populations is what makes that difference readable instead
 * of looking like an arithmetic error.
 *
 * Presentational: every value arrives as a prop, already mapped to display labels.
 */
export const TotalRoiScopeTooltip = ({
    roiName,
    includedVariables,
    spendWithoutContribution,
    children,
}: {
    /** The project's ROI flavour, e.g. `ROI` or `Cost per unit`. */
    roiName: string;
    /** Variables with both spend and contribution — the ones the value covers. */
    includedVariables: string[];
    /** Variables with spend but no contribution — left out of the value. */
    spendWithoutContribution: string[];
    /** The KPI value itself, which becomes the hover target. */
    children: ReactNode;
}) => (
    <Tooltip placement="bottom">
        <Tooltip.Trigger>
            <span className="cursor-help decoration-dotted underline underline-offset-4">
                {children}
            </span>
        </Tooltip.Trigger>
        <Tooltip.Content
            className={cn(
                'tooltip-content',
                // Three-column lists need the room; two-column ones read better
                // without it, since the prose fills to the same width.
                isWide(includedVariables) || isWide(spendWithoutContribution)
                    ? 'max-w-3xl'
                    : 'max-w-xl',
            )}
        >
            {/* `as="div"` because Typography renders a <p> by default, which
                cannot legally contain the paragraphs and list below. */}
            <Typography as="div" variant="bodyM" color="black">
                <p className="tooltip-header">
                    Calculated across {includedVariables.length}{' '}
                    {includedVariables.length === 1 ? 'variable' : 'variables'}
                </p>
                {includedVariables.length > 0 ? (
                    <>
                        <p>
                            Total Media {roiName} covers only variables that have
                            both spend and contribution in the selected period:
                        </p>
                        <VariableList variables={includedVariables} />
                    </>
                ) : (
                    <p>
                        No variable has both spend and contribution in the
                        selected period, so there is nothing for Total Media{' '}
                        {roiName} to be calculated over.
                    </p>
                )}
                {spendWithoutContribution.length > 0 && (
                    <>
                        <p className="mt-2">
                            Left out, because they have spend but no
                            contribution:
                        </p>
                        <VariableList variables={spendWithoutContribution} />
                    </>
                )}
                <p className="mt-2">
                    The table below still lists and totals every variable, so its
                    footer {roiName} can differ from this value.
                </p>
            </Typography>
        </Tooltip.Content>
    </Tooltip>
);
